'use client';

import React, { useCallback, useState } from 'react';
import debounce from 'debounce';
import { useTranslations } from 'next-intl';
import { useHttp } from '@/shared/api/provider';
import { Button } from '@/shared/ui/button';
import Input from '@/shared/ui/input';
import { MedcenterServicesList } from './MedcenterServicesList';
import baseIcons from '@/shared/assets/sprites/base.sprite.svg';
import type { IMedcenterService } from '@/entities/medcenter/model/types';
interface Props {
  initServices: IMedcenterService[];
  medcenterAlias: string;
}
export const MedcenterServicesContent: React.FC<Props> = ({
  initServices,
  medcenterAlias
}) => {
  const t = useTranslations();
  const http = useHttp();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [services, setServices] = useState(initServices);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const fetchServices = async (query: string, pageNum: number) => {
    setIsLoading(true);
    try {
      const response = query.length >= 2 ? await http.medcenter.searchMedcenterServices(medcenterAlias, {
        searchTerm: query,
        page: pageNum
      }) : await http.medcenter.getMedcenterServiceByAlias(medcenterAlias, pageNum);
      if (pageNum === 1) {
        setServices(response.data);
      } else {
        setServices(prev => [...prev, ...response.data]);
      }
      setHasNextPage(response.pagination.hasNextPage);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const debouncedSearch = useCallback(debounce((query: string) => {
    fetchServices(query, 1);
  }, 300), []);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setPage(1);
    debouncedSearch(query);
  };
  const showMore = async () => {
    if (!hasNextPage || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchServices(searchQuery, nextPage);
  };
  return <div className="flex flex-col" data-sentry-component="MedcenterServicesContent" data-sentry-source-file="MedcenterServicesContent.tsx">
      <div className="relative my-4">
        <svg className="absolute left-3 top-1/2 z-10 size-6 -translate-y-1/2 stroke-slate-950" data-sentry-element="svg" data-sentry-source-file="MedcenterServicesContent.tsx">
          <use xlinkHref={`${baseIcons}#search`} data-sentry-element="use" data-sentry-source-file="MedcenterServicesContent.tsx" />
        </svg>

        <Input type="search" placeholder={t('medcenter.services.search_placeholder')} value={searchQuery} onChange={handleSearchChange} className="w-full pl-10" data-sentry-element="Input" data-sentry-source-file="MedcenterServicesContent.tsx" />
      </div>

      <MedcenterServicesList list={services} data-sentry-element="MedcenterServicesList" data-sentry-source-file="MedcenterServicesContent.tsx" />

      {hasNextPage && searchQuery.length < 2 && <Button loading={isLoading} className="mt-4" variant="secondary-main" onClick={showMore}>
          {t('search.show_more')}
        </Button>}
    </div>;
};